import React from 'react'
import { Table, Space, Button, Tooltip, Tag, Badge, Popconfirm } from 'antd'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { getStockStatus, getExpiryStatus, formatCurrency, formatDate } from '../../utils/helpers'
import { mockCategories, mockDepartments } from '../../utils/mockInventoryData'

const InventoryTable = ({
  filteredItems,
  loading,
  sortedInfo,
  handleTableChange,
  pageSize,
  setPageSize,
  showEditModal,
  deleteInventoryItem
}) => {
  // Define table columns
  const columns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
      render: (text, record) => (
        <Space>
          {/* Show status badges */}
          {record.quantityOnHand < record.minStockLevel && <Badge color='orange' />}
          {record.expiryDate && getExpiryStatus(record.expiryDate).status === 'Expired' && <Badge color='red' />}
          {record.expiryDate && getExpiryStatus(record.expiryDate).status === 'Expiring Soon' && (
            <Badge color='volcano' />
          )}
          <span>{text}</span>
        </Space>
      ),
      width: '20%'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => a.category.localeCompare(b.category),
      sortOrder: sortedInfo.columnKey === 'category' && sortedInfo.order,
      filters: mockCategories.map((cat) => ({ text: cat.name, value: cat.name })),
      onFilter: (value, record) => record.category === value,
      width: '12%'
    },
    {
      title: 'Quantity',
      dataIndex: 'quantityOnHand',
      key: 'quantityOnHand',
      sorter: (a, b) => a.quantityOnHand - b.quantityOnHand,
      sortOrder: sortedInfo.columnKey === 'quantityOnHand' && sortedInfo.order,
      render: (quantity, record) => {
        const status = getStockStatus(record)
        return (
          <Tooltip title={`Min: ${record.minStockLevel} | Max: ${record.maxStockLevel}`}>
            <Tag color={status.color}>
              {quantity} {status.status !== 'Healthy' && `(${status.status})`}
            </Tag>
          </Tooltip>
        )
      },
      width: '12%'
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unitCost',
      key: 'unitCost',
      sorter: (a, b) => a.unitCost - b.unitCost,
      sortOrder: sortedInfo.columnKey === 'unitCost' && sortedInfo.order,
      render: (cost) => formatCurrency(cost),
      width: '10%'
    },
    {
      title: 'Supplier',
      dataIndex: ['supplier', 'name'],
      key: 'supplier',
      sorter: (a, b) => a.supplier.name.localeCompare(b.supplier.name),
      sortOrder: sortedInfo.columnKey === 'supplier' && sortedInfo.order,
      width: '15%'
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      sorter: (a, b) => a.department.localeCompare(b.department),
      sortOrder: sortedInfo.columnKey === 'department' && sortedInfo.order,
      filters: mockDepartments.map((dept) => ({ text: dept.name, value: dept.code })),
      onFilter: (value, record) => record.department === value,
      width: '12%'
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      sorter: (a, b) => {
        if (!a.expiryDate) return 1
        if (!b.expiryDate) return -1
        return new Date(a.expiryDate) - new Date(b.expiryDate)
      },
      sortOrder: sortedInfo.columnKey === 'expiryDate' && sortedInfo.order,
      render: (date) => {
        if (!date) return 'N/A'
        const status = getExpiryStatus(date)
        return <Tag color={status.color}>{formatDate(date)}</Tag>
      },
      width: '12%'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size='small' className='inventory-table-actions'>
          <Tooltip title='Edit'>
            <Button type='text' icon={<EditOutlined />} onClick={() => showEditModal(record)} />
          </Tooltip>
          <Tooltip title='Delete'>
            <Popconfirm
              title='Are you sure you want to delete this item?'
              onConfirm={() => deleteInventoryItem(record.id)}
              okText='Yes'
              cancelText='No'
            >
              <Button type='text' danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
      width: '10%'
    }
  ]

  return (
    <Table
      columns={columns}
      dataSource={filteredItems}
      rowKey='id'
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        pageSize: pageSize,
        pageSizeOptions: ['10', '20', '50', '100'],
        showSizeChanger: true,
        onShowSizeChange: (current, size) => setPageSize(size)
      }}
      className='inventory-table'
      size='middle'
      scroll={{ x: true }}
    />
  )
}

InventoryTable.propTypes = {
  filteredItems: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  sortedInfo: PropTypes.object,
  handleTableChange: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPageSize: PropTypes.func.isRequired,
  showEditModal: PropTypes.func.isRequired,
  deleteInventoryItem: PropTypes.func.isRequired
}

export default InventoryTable
